import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { PersistentDrawer } from "@/components/Drawer";
import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { HotelBookingSuccessWhatsAppBanner } from "@/components/WhatsApp/WhatsAppBanners";
import { reservationQueryOptions } from "@/features/bookings/hooks/useReservations";
import { BookedHotelCard } from "@/features/hotels/components/hotel/BookedHotelCard";
import { HotelMapDirections } from "@/features/hotels/components/hotel/HotelMapDirections";
import { TripEssentials } from "@/features/hotels/components/hotel/TripEssentials";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import { useHotelTravellersSelectionContext } from "@/features/hotels/contexts/HotelTravellersSelectionContext";
import { useOrder } from "@/hooks/useOrder";
import type { FindHotelsHotelItemV2 } from "@/types/codegen";

export const HotelBookingSuccess = ({
  orderId,
  hotel,
}: {
  orderId: string;
  hotel: FindHotelsHotelItemV2;
}) => {
  const navigate = useNavigate();

  const intl = useIntl();
  const { searchData } = useHotelSearchDataContext();
  const { selectedTravellers, leadTravellerId } =
    useHotelTravellersSelectionContext();
  const { data: orderData, isLoading: isOrderLoading } = useOrder(orderId);
  const reservationId = orderData?.reservations?.find(
    (reservation) => reservation.service === "hotel",
  )?.id;
  const { data: reservationData, isLoading: isReservationLoading } = useQuery(
    reservationQueryOptions(reservationId),
  );

  const isLoading =
    isOrderLoading ||
    !orderData?.reservations.length ||
    isReservationLoading ||
    !reservationData?.hotelReservation ||
    reservationData.hotelReservation.status === "IN_PROGRESS";

  if (isLoading) {
    return (
      <PageLoader>
        <AnimatedLoader icon="buildings" />
        <div className="text-sm font-semibold text-text-primary">
          {intl.formatMessage({
            defaultMessage: "Securing your hotel now...",
            description: "Page loading text on hotel booking success screen",
          })}
        </div>
      </PageLoader>
    );
  }

  return (
    <div className="flex flex-col gap-2.5 px-main">
      <BookedHotelCard
        reservation={reservationData.hotelReservation.externalReservation}
        hotel={hotel}
        travellers={selectedTravellers}
        leadTravellerId={leadTravellerId}
        orderData={orderData}
      />
      <TripEssentials
        reservation={reservationData.hotelReservation.externalReservation}
        destination={searchData?.destination}
      />
      <HotelMapDirections hotel={hotel} />
      <HotelBookingSuccessWhatsAppBanner />

      <PersistentDrawer>
        <Button
          variant="contained"
          fullWidth
          onClick={() =>
            navigate({ to: "/bookings", search: { category: "hotels" } })
          }
        >
          {intl.formatMessage({
            defaultMessage: "Manage My Trips",
            description: "Confirm button label on hotel booking success screen",
          })}
        </Button>
      </PersistentDrawer>
    </div>
  );
};
